import React, { useState } from 'react';

import classNames from 'classnames';
import { Button, Col, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SplitPane from 'react-split-pane';
import Swal from 'sweetalert2';
import SwalDialog from 'lib/utils/toast';
import { useHistory, useParams } from 'react-router-dom';

import ActionDropDown from 'components/shared/ActionDropDown';
import { areYouSure, exportSuccessDialog } from 'lib/utils/toast';
import Container from 'components/shared/Container';
import {
  getURL,
  GLOBAL_VENDOR_COMMENTS_GET,
  GLOBAL_VENDORS_CREATE_DEMO_CATALOG,
  GLOBAL_VENDORS_GET_BY_ID,
  GLOBAL_VENDORS_BROKEN_IMAGE_DELETE,
  GLOBAL_VENDORS_UPDATE,
  FILE_EXPORTS_MANUFACTURERS_GLN_STATUS_ASSOCIATED_WITH_A_VENDOR,
  FILE_EXPORTS_MASTER_LOCATION_PRODUCT_DETAILS,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import LogoUpdateModal from 'components/shared/LogoUpdateModal';
import pointerOnHover from 'lib/css/pointerOnHover';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import VendorCommentModal from 'components/manage_vendors/vendor_details/VendorCommentModal';
import VendorDetailSplitView from 'components/manage_vendors/vendor_details/VendorDetailSplitView';
import VendorLogoSplitView from 'components/manage_vendors/vendor_details/VendorLogoSplitView';
import VendorStatsBar from 'components/manage_vendors/vendor_details/VendorStatsBar';
import usePost from 'lib/hooks/usePost';

function VendorDetailsView() {
  const hasUserPermissions = useUserPermissions();
  const { vendorID } = useParams();
  const history = useHistory();
  const [showVendorLogoModal, setShowVendorLogoModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);

  const {
    data: { vendor } = {},
    loading,
    error,
    refetch,
  } = useGet(getURL(GLOBAL_VENDORS_GET_BY_ID, { vendor_id: vendorID }), {}, !vendorID);

  const { data: { data: comments = [] } = {}, refetch: refetchComments } = useGet(
    getURL(GLOBAL_VENDOR_COMMENTS_GET, { global_vendor_id: vendorID }),
    {},
    !vendorID,
  );

  const { postData: deleteBrokenImages } = usePost(
    GLOBAL_VENDORS_BROKEN_IMAGE_DELETE,
    () =>
      Swal.fire({
        title: 'Success',
        text: `Started removing broken image links of ${vendor.name}.`,
        icon: 'success',
      }),
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  const { postData: createDemoCatalog } = usePost(
    GLOBAL_VENDORS_CREATE_DEMO_CATALOG,
    () =>
      Swal.fire({
        title: 'Success',
        text: `Started creating demo catalog for ${vendor.name}.`,
        icon: 'success',
      }),
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  const { postData: exportManufacturersGLNStatus } = usePost(
    FILE_EXPORTS_MANUFACTURERS_GLN_STATUS_ASSOCIATED_WITH_A_VENDOR,
    () => exportSuccessDialog(),
    () =>
      SwalDialog(
        'error',
        'An error occurred while trying to download the manufacturers GLN status',
        'Error',
        'center',
      ),
  );

  const { postData: exportMasterLocationProductDetails } = usePost(
    FILE_EXPORTS_MASTER_LOCATION_PRODUCT_DETAILS,
    () => exportSuccessDialog(),
    error =>
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || 'Something went wrong, please try again.',
        icon: 'error',
      }),
  );

  const { postData: updateVendor, loading: updateLoading } = usePost(
    GLOBAL_VENDORS_UPDATE,
    () => {
      SwalDialog('success', 'Successfully updated distributor', 'Success', 'center', refetch());
    },
    error => {
      SwalDialog(
        'error',
        `An error occurred while updating the distributor: ${error?.response?.data?.message}`,
        'Error',
        'center',
      );
    },
  );

  const promptForComment = () => {
    Swal.fire({
      title: 'Add a comment?',
      text: 'Would you like to add a comment explaining this change?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Add Comment',
      cancelButtonText: 'Dismiss',
    }).then(result => {
      if (result.isConfirmed) {
        setShowCommentModal(true);
      }
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !vendor) {
    return null;
  }

  return (
    <Container>
      <Row>
        <Col xs={4}>
          <h3 className="font-weight-bold">
            <FontAwesomeIcon
              className={classNames(pointerOnHover, 'mr-2')}
              icon={faArrowCircleLeft}
              onClick={() => history.push('/enrich-products/manage-vendors')}
            />
            All Distributors
          </h3>
        </Col>
        <Col className="d-flex mr-5 justify-content-end align-items-center">
          <Button variant="primary" className="mr-2" onClick={() => setShowCommentModal(true)}>
            Comments{' '}
            {comments.length > 0 && (
              <span className="badge badge-light ml-1">{comments.length}</span>
            )}
          </Button>
          <ActionDropDown
            submenuItems={[
              {
                title: 'Remove Broken images',
                action: () => deleteBrokenImages({ vendor_id: vendorID }),
              },
              {
                title: 'Create Demo Catalog',
                action: () =>
                  areYouSure(
                    () => createDemoCatalog({ vendor_id: vendorID }),
                    `Are you sure you want to create a demo catalog for ${vendor.name}?`,
                  ),
                disabled: !vendor.is_test_vendor,
              },
              {
                title: 'Export Manufacturers GLN Status',
                action: () => exportManufacturersGLNStatus({ global_vendor_id: vendorID }),
                disabled: !hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]),
                tooltip:
                  'Export GLN status information for all manufacturers associated with this' +
                  ' distributors active products which has a gtin',
              },
              {
                title: 'Export Master Location Product Details',
                action: () => exportMasterLocationProductDetails({ global_vendor_id: vendorID }),
                disabled:
                  !vendor.is_mdm_vendor ||
                  !hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]),
                tooltip:
                  'Export product details for all products associated with this' +
                  ' distributor and its chained locations',
              },
            ]}
          />
        </Col>
      </Row>
      <VendorStatsBar vendorId={vendorID} />
      <SplitPane
        primary="first"
        split="vertical"
        defaultSize="25%"
        className={css(styles.splitPane)}
        allowResize={false}
      >
        <VendorLogoSplitView
          vendor={vendor}
          refetch={refetch}
          setShowVendorLogoModal={setShowVendorLogoModal}
        />
        <VendorDetailSplitView
          vendor={vendor}
          refetch={refetch}
          onVendorUpdated={promptForComment}
        />
      </SplitPane>
      {showVendorLogoModal ? (
        <LogoUpdateModal
          product={vendor}
          update={updateVendor}
          onHide={() => {
            setShowVendorLogoModal(false);
            refetch();
          }}
          loading={updateLoading}
        />
      ) : null}
      <VendorCommentModal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        globalVendorId={parseInt(vendorID)}
        vendorName={vendor.name}
        onCommentUpdated={refetchComments}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  splitPane: {
    position: 'relative',
    minHeight: '80vh',
  },
});

export default VendorDetailsView;
